import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Check,
  Loader2,
  X as XIcon,
} from "lucide-react";
import { API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const CURRENCIES = [
  "SEK", "USD", "EUR", "GBP", "NOK", "DKK",
  "SAR", "AED", "QAR", "KWD", "OMR", "BHD",
];

const STRENGTH_LABELS = ["weak", "weak", "fair", "good", "strong"];

function strength(pw) {
  let s = 0;
  if (pw.length >= 8) s += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s += 1;
  if (/\d/.test(pw)) s += 1;
  if (/[^a-zA-Z0-9]/.test(pw)) s += 1;
  return s;
}

function StepBadge({ index, active, done, label }) {
  return (
    <div className="flex items-center gap-2" data-testid={`signup-step-${index}`}>
      <div
        className={[
          "flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition-colors",
          done
            ? "bg-emerald-500 text-white"
            : active
              ? "bg-indigo-600 text-white ring-4 ring-indigo-100"
              : "bg-slate-200 text-slate-600",
        ].join(" ")}
      >
        {done ? <Check className="h-4 w-4" /> : index}
      </div>
      <span
        className={`hidden sm:block text-sm font-medium ${
          active || done ? "text-slate-900" : "text-slate-400"
        }`}
      >
        {label}
      </span>
    </div>
  );
}

function PlanCard({ plan, isChosen, lang, t }) {
  const perMonth = plan.code === "free"
    ? t("signup.valueProps.free")
    : `${plan.price_monthly} ${plan.currency}${t("signup.valueProps.perMonth")}`;
  return (
    <div
      className={[
        "rounded-2xl border p-4 transition",
        isChosen
          ? "border-indigo-500 bg-indigo-50/60 ring-2 ring-indigo-100"
          : "border-slate-200 bg-white",
      ].join(" ")}
      data-testid={`signup-plan-${plan.code}`}
    >
      <div className="flex items-baseline justify-between">
        <div className="font-heading text-lg text-slate-900">{plan.name}</div>
        <div className="text-sm font-semibold text-slate-800 tabular-nums">
          {perMonth}
        </div>
      </div>
      {plan.description && (
        <p className="mt-1 text-xs text-slate-500">{plan.description}</p>
      )}
      <ul className="mt-3 space-y-1.5 text-xs text-slate-700">
        {(plan.features || []).slice(0, 4).map((f) => (
          <li key={f.key} className="flex items-center gap-1.5">
            {f.included ? (
              <BadgeCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
            ) : (
              <XIcon className="h-3.5 w-3.5 text-slate-300 shrink-0" />
            )}
            <span className={f.included ? "" : "text-slate-400 line-through"}>
              {f.label}
            </span>
          </li>
        ))}
      </ul>
      {isChosen && (
        <div className="mt-3 inline-flex rounded-full bg-indigo-600 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white">
          {t("signup.valueProps.youreSigningUp")}
        </div>
      )}
      {!isChosen && (
        <div className="mt-3 text-[11px] text-slate-500">
          {t("signup.valueProps.upgradeLater")}
        </div>
      )}
    </div>
  );
}

export default function Signup() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const { applySession } = useAuth();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    admin_name: "",
    admin_email: "",
    admin_password: "",
    company_name: "",
    default_currency: "SEK",
    default_language: (i18n.language || "en").split("-")[0],
    accept_terms: false,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Live email check state
  const [emailCheck, setEmailCheck] = useState({
    checking: false,
    available: null,
    email: "",
  });

  // Live plans (from public endpoint)
  const [plans, setPlans] = useState([]);
  const lang = (i18n.language || "en").split("-")[0];
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_BASE}/subscription-plans`, { params: { lang } })
      .then(({ data }) => {
        if (!cancelled) setPlans(data.items || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const pwStrength = strength(form.admin_password);
  const pwStrengthLabel = STRENGTH_LABELS[Math.min(pwStrength, 4)];

  // Debounced email availability check
  useEffect(() => {
    const email = form.admin_email.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailCheck({ checking: false, available: null, email });
      return undefined;
    }
    setEmailCheck((s) => ({ ...s, checking: true, email }));
    const timeout = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API_BASE}/auth/check-email`, { email });
        setEmailCheck({
          checking: false,
          available: !!data.available,
          email,
        });
      } catch {
        setEmailCheck({ checking: false, available: null, email });
      }
    }, 400);
    return () => clearTimeout(timeout);
  }, [form.admin_email]);

  const step1Valid = useMemo(() => {
    const emailOk =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.admin_email) &&
      emailCheck.available !== false;
    const pwOk =
      form.admin_password.length >= 8 &&
      /[a-zA-Z]/.test(form.admin_password) &&
      /\d/.test(form.admin_password);
    return form.admin_name.trim().length > 1 && emailOk && pwOk;
  }, [form.admin_name, form.admin_email, form.admin_password, emailCheck.available]);

  const step2Valid = form.company_name.trim().length > 0;

  async function submit() {
    setErr(null);
    if (!form.accept_terms) return setErr(t("signup.errors.terms"));
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_BASE}/auth/signup`, form);
      applySession(data.access_token, data.user, data.company);
      toast.success(t("signup.success"));
      nav("/app");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 || d?.error === "email_taken") {
        setErr(t("signup.errors.emailTaken"));
        setStep(1);
      } else if (d?.error === "weak_password") {
        setErr(t("signup.errors.weakPassword"));
        setStep(1);
      } else {
        setErr(typeof d === "string" ? d : t("signup.errors.generic"));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 py-8"
      data-testid="signup-page"
    >
      <div className="mx-auto max-w-6xl px-4">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="signup-brand">
            <img src="/branding/icon-mark.jpeg" alt="InvoicePro" className="h-8 w-8 rounded" />
            <span className="font-heading text-lg font-semibold">InvoicePro</span>
          </Link>
          <LanguageSwitcher testId="signup-lang" />
        </div>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
          {/* Wizard form column */}
          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            {/* Stepper */}
            <div className="mb-6 flex items-center gap-3 sm:gap-6" data-testid="signup-stepper">
              <StepBadge index={1} active={step === 1} done={step > 1} label={t("signup.steps.account")} />
              <div className={`h-px flex-1 ${step > 1 ? "bg-emerald-400" : "bg-slate-200"}`} />
              <StepBadge index={2} active={step === 2} done={step > 2} label={t("signup.steps.company")} />
              <div className={`h-px flex-1 ${step > 2 ? "bg-emerald-400" : "bg-slate-200"}`} />
              <StepBadge index={3} active={step === 3} done={false} label={t("signup.steps.preferences")} />
            </div>

            {step === 1 && (
              <div data-testid="signup-step1">
                <h1 className="font-heading text-2xl sm:text-3xl">{t("signup.step1Title")}</h1>
                <p className="mt-1 text-sm text-slate-500">{t("signup.step1Subtitle")}</p>

                <div className="mt-6 space-y-4">
                  <div>
                    <Label>{t("signup.fields.name")}</Label>
                    <Input
                      required
                      autoFocus
                      value={form.admin_name}
                      onChange={(e) => setForm({ ...form, admin_name: e.target.value })}
                      data-testid="signup-name"
                    />
                  </div>
                  <div>
                    <Label>{t("signup.fields.email")}</Label>
                    <div className="relative">
                      <Input
                        required
                        type="email"
                        value={form.admin_email}
                        onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                        data-testid="signup-email"
                        className={
                          emailCheck.available === false
                            ? "border-red-400 focus-visible:ring-red-300"
                            : emailCheck.available === true
                              ? "border-emerald-400 focus-visible:ring-emerald-300"
                              : ""
                        }
                      />
                      <div className="pointer-events-none absolute end-3 top-1/2 -translate-y-1/2">
                        {emailCheck.checking ? (
                          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                        ) : emailCheck.available === true ? (
                          <Check className="h-4 w-4 text-emerald-500" />
                        ) : emailCheck.available === false ? (
                          <XIcon className="h-4 w-4 text-red-500" />
                        ) : null}
                      </div>
                    </div>
                    {emailCheck.checking && (
                      <div className="mt-1 text-xs text-slate-500" data-testid="signup-email-checking">
                        {t("signup.checking")}
                      </div>
                    )}
                    {!emailCheck.checking && emailCheck.available === true && (
                      <div className="mt-1 text-xs text-emerald-600" data-testid="signup-email-available">
                        {t("signup.emailAvailable")}
                      </div>
                    )}
                    {!emailCheck.checking && emailCheck.available === false && (
                      <div className="mt-1 text-xs text-red-600" data-testid="signup-email-taken">
                        {t("signup.emailTakenLive")} ·{" "}
                        <Link to="/login" className="underline">
                          {t("signup.loginInstead")}
                        </Link>
                      </div>
                    )}
                  </div>
                  <div>
                    <Label>{t("signup.fields.password")}</Label>
                    <Input
                      required
                      type="password"
                      value={form.admin_password}
                      onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                      data-testid="signup-password"
                    />
                    <div className="mt-1 flex gap-1" data-testid="signup-strength-meter">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className={[
                            "h-1.5 flex-1 rounded transition-colors",
                            i <= pwStrength
                              ? pwStrength >= 4
                                ? "bg-emerald-500"
                                : pwStrength === 3
                                  ? "bg-lime-500"
                                  : pwStrength === 2
                                    ? "bg-amber-500"
                                    : "bg-red-500"
                              : "bg-slate-200",
                          ].join(" ")}
                        />
                      ))}
                    </div>
                    <div className="mt-1 flex justify-between text-xs">
                      <span className="text-slate-500">{t("signup.passwordHint")}</span>
                      {form.admin_password && (
                        <span
                          className={
                            pwStrength >= 4
                              ? "text-emerald-600 font-medium"
                              : pwStrength === 3
                                ? "text-lime-600 font-medium"
                                : pwStrength === 2
                                  ? "text-amber-600 font-medium"
                                  : "text-red-600 font-medium"
                          }
                          data-testid="signup-strength-label"
                        >
                          {t(`signup.strength.${pwStrengthLabel}`)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <Button
                    onClick={() => step1Valid && setStep(2)}
                    disabled={!step1Valid}
                    data-testid="signup-next-1"
                    className="bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    {t("signup.next")} <ArrowRight className="ms-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="signup-step2">
                <h1 className="font-heading text-2xl sm:text-3xl">{t("signup.step2Title")}</h1>
                <p className="mt-1 text-sm text-slate-500">{t("signup.step2Subtitle")}</p>
                <div className="mt-6 space-y-4">
                  <div>
                    <Label>{t("signup.fields.company")}</Label>
                    <Input
                      required
                      autoFocus
                      value={form.company_name}
                      onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                      data-testid="signup-company"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>{t("signup.fields.currency")}</Label>
                      <Select
                        value={form.default_currency}
                        onValueChange={(v) => setForm({ ...form, default_currency: v })}
                      >
                        <SelectTrigger data-testid="signup-currency">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t("signup.fields.language")}</Label>
                      <Select
                        value={form.default_language}
                        onValueChange={(v) => setForm({ ...form, default_language: v })}
                      >
                        <SelectTrigger data-testid="signup-language">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en">English</SelectItem>
                          <SelectItem value="sv">Svenska</SelectItem>
                          <SelectItem value="ar">العربية</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex justify-between gap-2">
                  <Button variant="outline" onClick={() => setStep(1)} data-testid="signup-back-2">
                    <ArrowLeft className="me-1 h-4 w-4" /> {t("signup.back")}
                  </Button>
                  <Button
                    onClick={() => step2Valid && setStep(3)}
                    disabled={!step2Valid}
                    data-testid="signup-next-2"
                    className="bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    {t("signup.next")} <ArrowRight className="ms-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div data-testid="signup-step3">
                <h1 className="font-heading text-2xl sm:text-3xl">{t("signup.step3Title")}</h1>
                <p className="mt-1 text-sm text-slate-500">{t("signup.step3Subtitle")}</p>
                <dl className="mt-6 divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-slate-50/60 text-sm">
                  {[
                    ["email", form.admin_email],
                    ["name", form.admin_name],
                    ["company", form.company_name],
                    ["currency", form.default_currency],
                    ["language", form.default_language.toUpperCase()],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between px-4 py-3">
                      <dt className="text-slate-500">{t(`signup.reviewLabels.${k}`)}</dt>
                      <dd className="font-medium text-slate-900" data-testid={`signup-review-${k}`}>{v}</dd>
                    </div>
                  ))}
                </dl>
                <label className="mt-5 flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.accept_terms}
                    onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })}
                    data-testid="signup-terms"
                    className="mt-0.5 h-4 w-4 accent-indigo-600"
                  />
                  <span>
                    {t("signup.acceptTerms")}{" "}
                    <Link to="/legal/terms" className="text-indigo-700 hover:underline" target="_blank" rel="noreferrer">
                      {t("signup.terms")}
                    </Link>
                  </span>
                </label>

                {err && (
                  <div
                    className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700"
                    data-testid="signup-error"
                  >
                    {err}
                  </div>
                )}

                <div className="mt-6 flex justify-between gap-2">
                  <Button variant="outline" onClick={() => setStep(2)} data-testid="signup-back-3">
                    <ArrowLeft className="me-1 h-4 w-4" /> {t("signup.back")}
                  </Button>
                  <Button
                    onClick={submit}
                    disabled={busy || !form.accept_terms}
                    data-testid="signup-submit"
                    className="bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("signup.submit")}
                  </Button>
                </div>
              </div>
            )}

            <p className="mt-6 text-center text-sm text-slate-500">
              {t("signup.haveAccount")}{" "}
              <Link to="/login" className="text-indigo-700 hover:underline">
                {t("signup.login")}
              </Link>
            </p>
          </div>

          {/* Value proof column */}
          <aside className="space-y-3" data-testid="signup-value-props">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {t("signup.valueProps.title")}
            </div>
            {plans.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400">
                …
              </div>
            ) : (
              plans.map((plan) => (
                <PlanCard
                  key={plan.code}
                  plan={plan}
                  isChosen={plan.code === "free"}
                  lang={lang}
                  t={t}
                />
              ))
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
